import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { getGuideAccess, setGuideAccess, unlockGuideAccess } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { StayGuideAccess } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { CopyValueButton } from '../../../copy-value/copy-value-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { formatDateTime, formatTime } from '../../../../function-utils/locale';
import toast from '../../../../utils/toast';
import { palette, spacing, typography } from '../../../../style-utils/theme';
import { deviceClock } from './guide-model';

/**
 * ONE STAY'S GUIDE ROW (plan S7.6), on S6's per-stay check-in card. stays:manage
 * only — the PIN is the host's to read and send, so the row is not drawn for
 * anyone else.
 *   "Guide PIN: off" | "Guide PIN: 51937" + "Copy guide PIN";
 *   "Turn PIN on" | "New PIN" + "Turn PIN off" (asks first);
 *   locked after five wrong PINs: a warning pill with the time + "Unlock now";
 *   "Door codes first shown 14:32 · shown 3 times" | "Door codes not shown yet".
 * Times are on the host's DEVICE clock (R-T12), with its clock note.
 * The PIN is never logged, and never in an aria-label / title / data-*.
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Strong = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Quiet = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const lockedNow = (a: StayGuideAccess | null, now: number): boolean =>
  !!(a && a.locked_until && Date.parse(a.locked_until) > now);

export interface StayGuideRowProps {
  reservationId: string;
  canManage: boolean;
}

export const StayGuideRow: React.FC<StayGuideRowProps> = ({ reservationId, canManage }) => {
  const [access, setAccess] = useState<StayGuideAccess | null>(null);
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [busy, setBusy] = useState<'on' | 'new' | 'off' | 'unlock' | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const a = await getGuideAccess(reservationId);
      if (mine !== seq.current) return;
      if (!a || typeof a.pin_required !== 'boolean') throw new Error('no answer');
      setAccess(a);
      setView('ready');
    } catch {
      if (mine === seq.current) setView('error');
    }
  }, [reservationId]);

  useEffect(() => {
    if (canManage) void load();

    return () => {
      seq.current += 1;
    };
  }, [canManage, load]);

  if (!canManage) return null;

  const act = async (key: 'on' | 'new' | 'off' | 'unlock', ok: string) => {
    if (busy) return;
    setBusy(key);
    try {
      const next =
        key === 'unlock'
          ? await unlockGuideAccess(reservationId)
          : await setGuideAccess(reservationId, key === 'off' ? { pin_required: false } : key === 'new' ? { pin_required: true, regenerate: true } : { pin_required: true });
      setAccess(next);
      setView('ready');
      toast.success(ok);
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not change the guide PIN. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  if (view === 'loading') return <Quiet>Loading the guide PIN…</Quiet>;
  if (view === 'error' || !access) {
    return (
      <Line>
        <Quiet>Couldn&apos;t load the guide PIN.</Quiet>
        <AppButton variant="ghost" size="compact" onClick={() => void load()}>
          Try again
        </AppButton>
      </Line>
    );
  }

  const locked = lockedNow(access, Date.now());
  const on = access.pin_required && !!access.pin;
  const r = access.revealed || { count: 0, first_at: null, last_at: null };
  const timed = locked || !!r.first_at;

  return (
    <Wrap data-testid="stay-guide-row">
      <Line>
        <Strong>{on ? `Guide PIN: ${access.pin}` : 'Guide PIN: off'}</Strong>
        {on && access.pin ? <CopyValueButton value={access.pin} label="Copy guide PIN" announce="Guide PIN copied" /> : null}
      </Line>
      <Line>
        {on ? (
          <>
            <AppButton variant="ghost" size="compact" loading={busy === 'new'} loadingLabel="Making a new PIN"
              disabled={!!busy && busy !== 'new'} onClick={() => void act('new', 'New guide PIN made')}>
              New PIN
            </AppButton>
            <AppButton variant="ghost" size="compact" disabled={!!busy} onClick={() => setConfirmOff(true)}>
              Turn PIN off
            </AppButton>
          </>
        ) : (
          <AppButton variant="ghost" size="compact" loading={busy === 'on'} loadingLabel="Turning the PIN on"
            disabled={!!busy && busy !== 'on'} onClick={() => void act('on', 'Guide PIN turned on')}>
            Turn PIN on
          </AppButton>
        )}
      </Line>
      {locked && access.locked_until ? (
        <Line>
          <StatusPill tone="warning" data-testid="stay-guide-locked">
            Locked after wrong PINs until <span data-at={access.locked_until}>{formatTime(access.locked_until)}</span>
          </StatusPill>
          <AppButton variant="ghost" size="compact" loading={busy === 'unlock'} loadingLabel="Unlocking"
            disabled={!!busy && busy !== 'unlock'} onClick={() => void act('unlock', 'Guide unlocked')}>
            Unlock now
          </AppButton>
        </Line>
      ) : null}
      <Line>
        {r.first_at ? (
          <Quiet data-testid="stay-guide-revealed">
            Door codes first shown <span data-at={r.first_at}>{formatDateTime(r.first_at)}</span>
            {` · shown ${r.count} ${r.count === 1 ? 'time' : 'times'}`}
          </Quiet>
        ) : (
          <Quiet data-testid="stay-guide-revealed">Door codes not shown yet</Quiet>
        )}
      </Line>
      {timed ? <ClockNote clock={deviceClock()} /> : null}
      <ConfirmDialog
        open={confirmOff}
        title="Turn the PIN off?"
        message="Anyone with the check-in link can open the guide."
        primaryLabel="Turn PIN off"
        dismissLabel="Keep the PIN"
        onPrimary={async () => {
          await act('off', 'Guide PIN turned off');
          setConfirmOff(false);
        }}
        onDismiss={() => setConfirmOff(false)}
      />
    </Wrap>
  );
};
